import { getStorageKey } from "../utils/localStorageStateUniqueKeyGenerator";
import { checkoutErrorMessages } from "../constants/errorMessages";
import {
  convertPointsToDollars,
  convertDollarsToPoints,
} from "../constants/rewardConstants";
/**
 * Generic validation function to validate fields from localStorage.
 */
export const validateFieldFromLocalStorage = (
  fieldKey,
  errorKey,
  errorMessage,
  currentContextType,
  setErrors
) => {
  const fieldValue = localStorage.getItem(getStorageKey(fieldKey, currentContextType));
  const error = fieldValue ? "" : errorMessage;

  // Update errors if the setter is provided
  if (setErrors) {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [errorKey]: error,
    }));
  }
  return !error; // Return true if valid
};

/**
 * Specific field validation functions leveraging checkoutErrorMessages.
 */
export const validatePaymentMethod = (currentContextType, setErrors) => {
  const paymentMethod = localStorage.getItem(
    getStorageKey("paymentMethod", currentContextType)
  );

  if (!paymentMethod) {
    setErrors((prevErrors) => ({
      ...prevErrors,
      paymentMethod: checkoutErrorMessages.paymentMethod,
    }));
    return false; // Validation failed
  }

  setErrors((prevErrors) => ({
    ...prevErrors,
    paymentMethod: null, // Clear error if validation passes
  }));
  return true; // Validation passed
};

export const validateExpirationMonth = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "ccExpMonth",
    "expirationMonth",
    checkoutErrorMessages.expirationMonth,
    currentContextType,
    setErrors
  );
};

export const validateExpirationYear = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "ccExpYear",
    "expirationYear",
    checkoutErrorMessages.expirationYear,
    currentContextType,
    setErrors
  );
};

export const validateCreditCardNumber = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "creditCardNumber",
    "creditCardNumber",
    checkoutErrorMessages.creditCardNumber,
    currentContextType,
    setErrors
  );
};
export const validateFirstName = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "firstName",
    "firstName",
    checkoutErrorMessages.firstName,
    currentContextType,
    setErrors
  );
};

export const validateLastName = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "lastName",
    "lastName",
    checkoutErrorMessages.lastName,
    currentContextType,
    setErrors
  );
};

export const validateAddress1 = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "address1",
    "address1",
    checkoutErrorMessages.address1,
    currentContextType,
    setErrors
  );
};

export const validateCity = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "city",
    "city",
    checkoutErrorMessages.city,
    currentContextType,
    setErrors
  );
};

export const validatePostalCode = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "postalCode",
    "postalCode",
    checkoutErrorMessages.postalCode,
    currentContextType,
    setErrors
  );
};

export const validateCountry = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "country",
    "country",
    checkoutErrorMessages.country,
    currentContextType,
    setErrors
  );
};

export const validateSelectedState = (currentContextType, setErrors) => {
  return validateFieldFromLocalStorage(
    "selectedState",
    "selectedState",
    checkoutErrorMessages.selectedState,
    currentContextType,
    setErrors
  );
};

export const calculateValues = ({
  totalBudget,
  paymentType,
  costPerImpression,
  costPerClick,
  merchantFee,
  pointsToUse,
  userPointsBalance,
}) => {
  let impressionsOrClicks = 0; // use let here because impressionsOrClicks needs to change depending on the paymentType, so const won't work.

  if (paymentType === "impressions") {
    impressionsOrClicks = Math.floor(totalBudget / costPerImpression);
  } else if (paymentType === "clicks") {
    impressionsOrClicks = Math.floor(totalBudget / costPerClick);
  }
  // using const below to recalculate the arguments giving and it is not re-assigning or binding to a new variable just recalculating
  const orderSubtotal = totalBudget;
  const merchantFeeAmount = orderSubtotal * merchantFee;
  const orderGrandTotal = orderSubtotal + merchantFeeAmount;

  const maxOrderPoints = Math.min(
    userPointsBalance,
    convertDollarsToPoints(orderGrandTotal) // Convert grand total dollars to points
  );

  const clampedPoints = Math.min(pointsToUse, maxOrderPoints);
  const redeemedDollars = convertPointsToDollars(clampedPoints); // Calculate total due
  const orderTotalDue = Math.max(orderGrandTotal - redeemedDollars, 0); // Subtract redeemed points in dollars

  return {
    impressionsOrClicks,
    orderSubtotal,
    merchantFeeAmount,
    orderGrandTotal,
    maxOrderPoints,
    clampedPoints,
    orderTotalDue,
  };
};
